import { useAgent } from '~/composables/agents'
import type {
  UserAgentState,
  UserSettings,
  TodoItem,
  Notification,
} from '~/types/user-agent'

type FavoriteItem = {
  id: string
  itemType: string
  itemId: string
  userId?: string
  createdAt?: string
}
import { createSharedComposable } from '@vueuse/core'

function _useUserAgent() {
  const config = useRuntimeConfig()
  const workersHost = config.public.WORKERS_API_BASE_URL as string
  const wsHost = workersHost.replace(/^http/, 'ws')

  const {
    state,
    sendMessage,
    updateState,
    reconnect,
    connectionState,
    connectionError,
    onMessage,
  } = useAgent<UserAgentState & Record<string, unknown>>({
    agent: 'user-agent',
    name: 'ws',
    host: wsHost,
    baseURL: '/api', // Required by useAgent, but we bypass its fetch client
  })

  const toast = useToast()
  const { $groundcover } = useNuxtApp()

  // Use a customized $fetch directly pointing to our Nuxt server proxy
  const userAgentFetch = $fetch.create({
    baseURL: '/api/user-agent',
    onRequestError({ error }) {
      $groundcover?.captureException(error)
    },
    onResponseError({ response, error }) {
      $groundcover?.captureException(
        error || new Error(`UserAgent API Error: ${response.status}`),
      )
    },
  })

  // Handle incoming real-time notifications
  onMessage((msg) => {
    if (msg.type === 'notification:new') {
      // e.g. toast.success(msg.data.title)
    }
  })

  const fetchSettings = async () => {
    return await userAgentFetch<UserSettings>('/settings')
  }

  const updateUserSettings = async (updates: Partial<UserSettings>) => {
    return await userAgentFetch<UserSettings>('/settings', {
      method: 'PUT',
      body: updates,
    })
  }

  const fetchTodos = async () => {
    return await userAgentFetch<TodoItem[]>('/todos')
  }

  const getTodo = (id: string) => userAgentFetch<TodoItem>(`/todos/${id}`)

  const createTodo = async (todo: {
    title: string
    description?: string
    dueDate?: string
  }) => {
    return await userAgentFetch<TodoItem>('/todos', {
      method: 'POST',
      body: todo,
    })
  }

  const updateTodo = async (id: string, updates: Partial<TodoItem>) => {
    return await userAgentFetch<TodoItem>(`/todos/${id}`, {
      method: 'PUT',
      body: updates,
    })
  }

  const deleteTodo = async (id: string) => {
    await userAgentFetch(`/todos/${id}`, { method: 'DELETE' })
  }

  const fetchNotifications = async (params?: {
    limit?: number
    offset?: number
    unread?: boolean
  }) => {
    return await userAgentFetch<{
      notifications: Notification[]
      unreadCount: number
      total: number
      limit: number
      offset: number
    }>('/notifications', { query: params })
  }

  const updateNotification = async (
    id: string,
    updates: { read?: boolean },
  ) => {
    return await userAgentFetch<Notification>(`/notifications/${id}`, {
      method: 'PATCH',
      body: updates,
    })
  }

  const deleteNotification = async (id: string) => {
    await userAgentFetch(`/notifications/${id}`, { method: 'DELETE' })
  }

  const markAllNotificationsRead = async () => {
    await userAgentFetch('/notifications/mark-all-read', { method: 'POST' })
  }

  const fetchFavorites = async (itemType?: FavoriteItem['itemType']) => {
    return await userAgentFetch<FavoriteItem[]>('/favorites', {
      method: 'GET',
      query: { itemType },
    })
  }

  const saveFavorite = async (
    itemType: FavoriteItem['itemType'],
    itemId: string,
  ) => {
    return await userAgentFetch<FavoriteItem>('/favorites', {
      method: 'POST',
      body: { itemType, itemId },
    })
  }

  const deleteFavoriteById = async (id: string) => {
    return await userAgentFetch<{ success: boolean }>(`/favorites/${id}`, {
      method: 'DELETE',
    })
  }

  const deleteFavoriteByItemType = async (
    itemType: FavoriteItem['itemType'],
    itemId: string,
  ) => {
    return await userAgentFetch<{ success: boolean }>('/favorites', {
      method: 'DELETE',
      query: { itemType, itemId },
    })
  }

  const ping = () => {
    sendMessage({ type: 'ping', data: {} })
  }

  // Computed views over the synchronized state
  const todos = computed(() => state.value?.todos || [])
  const notifications = computed(() => state.value?.notifications || [])
  const settings = computed(() => state.value?.settings)
  const unreadNotificationsCount = computed(
    () => notifications.value.filter((n) => !n.read).length,
  )
  const favorites = computed(() => state.value?.favorites ?? [])
  const favoritesByType = computed(() =>
    favorites.value.reduce((map, item) => {
      map.set(item.itemType, (map.get(item.itemType) ?? []).concat(item))

      return map
    }, new Map<FavoriteItem['itemType'], FavoriteItem[]>()),
  )
  const hasFavorite = (
    itemType: FavoriteItem['itemType'],
    itemId: string,
  ): boolean =>
    favoritesByType.value
      .get(itemType)
      ?.some((item) => item.itemId === itemId) ?? false

  const toggleFavorite = async (
    itemType: FavoriteItem['itemType'],
    itemId: string,
  ): Promise<boolean> => {
    try {
      if (hasFavorite(itemType, itemId)) {
        await deleteFavoriteByItemType(itemType, itemId)
      } else {
        await saveFavorite(itemType, itemId)
      }
      return true
    } catch (error) {
      $groundcover?.captureException(
        error instanceof Error
          ? error
          : new Error(`Failed to toggle favorite: ${String(error)}`),
      )
      toast.add({
        color: 'error',
        description: 'Failed to toggle favorite. Please try again.',
      })
      return false
    }
  }

  return {
    state,
    connectionState,
    connectionError,
    reconnect,
    updateState,
    ping,
    todos,
    notifications,
    settings,
    unreadNotificationsCount,
    favorites,
    fetchSettings,
    updateUserSettings,
    fetchTodos,
    getTodo,
    createTodo,
    updateTodo,
    deleteTodo,
    fetchNotifications,
    updateNotification,
    deleteNotification,
    markAllNotificationsRead,
    fetchFavorites,
    hasFavorite,
    saveFavorite,
    deleteFavoriteById,
    deleteFavoriteByItemType,
    toggleFavorite,
  }
}

export const useUserAgent = createSharedComposable(_useUserAgent)
